import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, ChevronUp, ChevronDown, Upload, Download } from 'lucide-react';
import { useCompanies, useDeleteCompany } from '../hooks/useCompanies.js';
import { toast } from '../lib/toast.js';
import type { Company, CompanyType } from '@mayksel/types';
import CompanyModal from '../components/companies/CompanyModal.js';
import CsvImportModal from '../components/dataManagement/CsvImportModal.js';
import ViewBar from '../components/views/ViewBar.js';
import type { FieldDef } from '../components/views/FilterPanel.js';
import type { ColumnDef } from '../components/views/ColumnPicker.js';
import { downloadCsv } from '../lib/csvExport.js';
import { useFeatureFlag } from '../lib/posthog.js';

type SortDir = 'asc' | 'desc';

const TYPE_OPTIONS: { value: CompanyType | ''; label: string }[] = [
  { value: '', label: 'list.all_types' },
  { value: 'prospect', label: 'types.prospect' },
  { value: 'customer', label: 'types.customer' },
  { value: 'partner', label: 'types.partner' },
  { value: 'vendor', label: 'types.vendor' },
];

const COMPANY_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', required: true },
  { key: 'type', label: 'Type' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'website', label: 'Website' },
  { key: 'city', label: 'City' },
];

function COMPANY_FIELD_DEFS(t: (key: string) => string): FieldDef[] {
  return [
    { key: 'type', label: t('list.filter_type'), type: 'select', options: [
      { value: 'prospect', label: t('types.prospect') },
      { value: 'customer', label: t('types.customer') },
      { value: 'partner', label: t('types.partner') },
      { value: 'vendor', label: t('types.vendor') },
    ]},
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
  ];
}

const TYPE_COLORS: Record<CompanyType, string> = {
  prospect: 'var(--color-info)',
  customer: 'var(--color-success)',
  partner: 'var(--color-brand)',
  vendor: 'var(--color-amber)',
};

export default function Companies() {
  const { t } = useTranslation('companies');
  const { t: tdm } = useTranslation('dataManagement');
  const navigate = useNavigate();
  const dataManagementEnabled = useFeatureFlag('data_management');

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading, error } = useCompanies({
    ...(search ? { search } : {}),
    ...(typeFilter ? { type: typeFilter } : {}),
    sortBy,
    sortDir,
  });

  const deleteCompany = useDeleteCompany();

  const handleSort = useCallback(
    (col: string) => {
      if (sortBy === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(col);
        setSortDir('asc');
      }
    },
    [sortBy],
  );

  function handleExport() {
    if (!data?.data.length) return;
    const rows = data.data.map((c) => ({
      name: c.name,
      type: c.type.join(', '),
      website: c.website ?? '',
      phone: c.phone ?? '',
      email: c.email ?? '',
      addressLine1: c.addressLine1 ?? '',
      city: c.city ?? '',
      state: c.state ?? '',
      zip: c.zip ?? '',
      country: c.country ?? '',
      notes: c.notes ?? '',
    }));
    downloadCsv(rows, 'companies');
    toast.success(tdm('export.button'));
  }

  async function handleDelete(company: Company) {
    if (!confirm(t('detail.delete_confirm', { name: company.name }))) return;
    try {
      await deleteCompany.mutateAsync(company.id);
      toast.success(t('toast.deleted'));
    } catch (_err) {
      toast.error(t('toast.delete_error'));
    }
  }

  function handleEdit(company: Company) {
    setEditingCompany(company);
    setModalOpen(true);
  }

  function handleModalClose() {
    setModalOpen(false);
    setEditingCompany(null);
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={14} style={{ marginLeft: 4 }} />
      : <ChevronDown size={14} style={{ marginLeft: 4 }} />;
  };

  return (
    <div className="page-enter">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <h1 className="page-title">{t('list.title')}</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {dataManagementEnabled && (
            <>
              <button
                onClick={() => setImportOpen(true)}
                style={secondaryBtnStyle}
              >
                <Upload size={14} />
                {tdm('import.button')}
              </button>
              <button
                onClick={handleExport}
                disabled={!data?.data.length}
                style={{ ...secondaryBtnStyle, opacity: data?.data.length ? 1 : 0.5 }}
              >
                <Download size={14} />
                {tdm('export.button')}
              </button>
            </>
          )}
          <button
            onClick={() => setModalOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
              color: 'var(--color-text-inverse)', border: 'none',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)', cursor: 'pointer',
            }}
          >
            + {t('list.add_button')}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.search_placeholder')}
            style={{
              width: '100%',
              paddingLeft: 36,
              paddingRight: 'var(--space-3)',
              paddingTop: 'var(--space-2)',
              paddingBottom: 'var(--space-2)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text)',
              background: 'var(--color-bg)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label={t('list.filter_type')}
          style={{
            padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text)',
            background: 'var(--color-bg)',
          }}
        >
          {TYPE_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {t(label)}
            </option>
          ))}
        </select>
      </div>

      {/* View bar */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <ViewBar
          entityType="company"
          columnDefs={COMPANY_COLUMNS}
          fieldDefs={COMPANY_FIELD_DEFS(t)}
          onViewChange={() => undefined}
        />
      </div>

      {/* Loading state */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="skeleton-line"
              style={{ height: 52, borderRadius: 'var(--radius-md)' }}
            />
          ))}
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div
          style={{
            padding: 'var(--space-6)',
            background: 'var(--color-danger-light)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-danger-text)',
            fontSize: 'var(--text-sm)',
            textAlign: 'center',
          }}
        >
          {t('errors.generic', { ns: 'common' })}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && data?.data.length === 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-16) var(--space-8)',
            textAlign: 'center',
            border: '1px dashed var(--color-border)',
            borderRadius: 'var(--radius-xl)',
          }}
        >
          <Building2 size={48} style={{ marginBottom: 'var(--space-4)', color: 'var(--color-gray-300)' }} />
          <h2
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)',
            }}
          >
            {search || typeFilter ? t('empty.no_results', { ns: 'common' }) : t('list.empty_title')}
          </h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', maxWidth: 320, marginBottom: 'var(--space-5)' }}>
            {search || typeFilter ? t('empty.try_adjusting', { ns: 'common' }) : t('list.empty_body')}
          </p>
          {!search && !typeFilter && (
            <button
              onClick={() => setModalOpen(true)}
              style={{
                padding: 'var(--space-2) var(--space-5)',
                background: 'var(--color-brand)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                cursor: 'pointer',
              }}
            >
              + {t('list.empty_cta')}
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && data && data.data.length > 0 && (
        <div
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            background: 'var(--color-bg)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)' }}>
                {[
                  { key: 'name', label: t('form.name_label') },
                  { key: 'type', label: t('form.type_label') },
                  { key: 'website', label: t('form.website_label') },
                  { key: 'phone', label: t('form.phone_label') },
                  { key: 'city', label: t('form.city_label') },
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => key !== 'type' && key !== 'website' && handleSort(key)}
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      textAlign: 'left',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--color-text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-wide)',
                      cursor: key !== 'type' && key !== 'website' ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {label}
                      <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {data.data.map((company) => (
                <tr
                  key={company.id}
                  onClick={() => navigate(`/companies/${company.id}`)}
                  style={{
                    borderBottom: '1px solid var(--color-gray-100)',
                    cursor: 'pointer',
                    transition: 'background var(--duration-snap)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                    {company.name}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                      {company.type.map((ct) => (
                        <span
                          key={ct}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px var(--space-2)',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-medium)',
                            background: `color-mix(in srgb, ${TYPE_COLORS[ct as CompanyType]} 12%, transparent)`,
                            color: TYPE_COLORS[ct as CompanyType],
                          }}
                        >
                          {t(`types.${ct}`)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {company.website ? (
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'var(--color-brand)', textDecoration: 'none' }}
                      >
                        {company.website.replace(/^https?:\/\//, '')}
                      </a>
                    ) : '—'}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {company.phone ?? '—'}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {[company.city, company.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td
                    style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(company)}
                        style={actionBtnStyle}
                        aria-label={`Edit ${company.name}`}
                      >
                        {t('detail.edit_button')}
                      </button>
                      <button
                        onClick={() => handleDelete(company)}
                        style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
                        aria-label={`Delete ${company.name}`}
                      >
                        {t('actions.delete', { ns: 'common' })}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {data.total > 50 && (
            <div
              style={{
                padding: 'var(--space-3) var(--space-4)',
                borderTop: '1px solid var(--color-border)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              {t('pagination.showing', { ns: 'common', from: 1, to: data.data.length, total: data.total })}
            </div>
          )}
        </div>
      )}

      {/* Company create/edit modal */}
      {modalOpen && (
        <CompanyModal
          company={editingCompany}
          onClose={handleModalClose}
        />
      )}

      {/* CSV import modal */}
      {importOpen && (
        <CsvImportModal
          entityType="company"
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  padding: '4px 8px',
  background: 'none',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
};

const secondaryBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
  padding: 'var(--space-2) var(--space-3)',
  background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)', cursor: 'pointer',
};
